import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { StudentNav } from '../../components/shared/BottomNav';
import {
  logOut,
  formatCurrency,
  subscribeToRecurringTemplates,
  deleteRecurringTemplate,
} from '../../services/firebase';

const StudentSettings = () => {
  const { userDoc, authUser, family } = useAuth();
  const navigate = useNavigate();
  const [saved, setSaved] = useState(false);
  const [recurringTemplates, setRecurringTemplates] = useState([]);
  const [deletingId, setDeletingId] = useState(null);

  const studentFirst = userDoc?.displayName?.split(' ')[0] || 'Student';
  const allowance = family?.allowance;
  const match = family?.match;

  useEffect(() => {
    if (!family?.id) return;
    const unsub = subscribeToRecurringTemplates(family.id, setRecurringTemplates);
    return unsub;
  }, [family?.id]);

  const handleSignOut = async () => {
    await logOut();
    navigate('/signin');
  };

  return (
    <div className="screen">
      <StudentNav />
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
      </div>

      {saved && (
        <div className="alert alert--success" style={{ marginBottom: 20 }}>✓ Saved</div>
      )}

      <div className="stack stack-4">

        {/* Profile */}
        <SettingsSection title="Your account">
          <SettingsRow icon="👤" label="Name" value={userDoc?.displayName} />
          <SettingsRow icon="✉️" label="Email" value={authUser?.email} />
          <SettingsRow icon="🎓" label="Role" value="Student" />
        </SettingsSection>

        {/* Wallet details */}
        <SettingsSection title={`${studentFirst}'s wallet`}>
          {allowance && (
            <SettingsRow
              icon="💰"
              label="Allowance"
              value={`${formatCurrency(allowance.amount)} ${allowance.frequency}`}
            />
          )}
          {match?.enabled && (
            <SettingsRow
              icon="🎯"
              label="Savings match"
              value={match.type === 'percentage'
                ? `${match.value}% of your savings`
                : `$${match.value} per dollar saved`}
            />
          )}
          {!match?.enabled && (
            <SettingsRow icon="🎯" label="Savings match" value="Not enabled" />
          )}
        </SettingsSection>

        {/* Recurring planned items */}
        {recurringTemplates.length > 0 && (
          <SettingsSection title="Recurring planned items">
            <div className="stack stack-3">
              {recurringTemplates.map(t => {
                const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                const CATS = [
                  { id: 'food', label: 'Food', emoji: '🍕' },
                  { id: 'groceries', label: 'Groceries', emoji: '🛒' },
                  { id: 'transport', label: 'Transport', emoji: '🚗' },
                  { id: 'entertainment', label: 'Fun', emoji: '🎬' },
                  { id: 'clothing', label: 'Clothing', emoji: '👕' },
                  { id: 'health', label: 'Health', emoji: '💊' },
                  { id: 'books', label: 'Books', emoji: '📚' },
                  { id: 'other', label: 'Other', emoji: '📦' },
                ];
                const cat = CATS.find(c => c.id === t.category);
                return (
                  <div key={t.id} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '6px 0', borderBottom: '1px solid var(--color-border)',
                  }}>
                    <span style={{ fontSize: 18 }}>{cat?.emoji || '📦'}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.9375rem' }}>
                        {t.note || cat?.label || t.category}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)', marginTop: 2 }}>
                        🔁 Every {DAYS[t.spendDay]} · {formatCurrency(t.amount)}
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        setDeletingId(t.id);
                        await deleteRecurringTemplate(family.id, t.id);
                        setDeletingId(null);
                      }}
                      disabled={deletingId === t.id}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: 'var(--color-error)', fontSize: '0.8125rem',
                        fontWeight: 600, fontFamily: 'var(--font-body)',
                        padding: '4px 8px', flexShrink: 0,
                      }}>
                      {deletingId === t.id
                        ? <span className="spinner" style={{ width: 14, height: 14 }} />
                        : 'Remove'}
                    </button>
                  </div>
                );
              })}
            </div>
            <p style={{ fontSize: '0.75rem', color: 'var(--color-text-3)',
              marginTop: 12, lineHeight: 1.5 }}>
              These items are offered at the start of each new week. Turn them off
              by editing the item and toggling "Repeat weekly".
            </p>
          </SettingsSection>
        )}

        {/* Bank account — coming in Phase 2 */}
        <SettingsSection title="Bank account">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <span style={{ fontSize: 20 }}>🏦</span>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 4 }}>
                Automatic bank sync
              </div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-2)',
                lineHeight: 1.5, marginBottom: 10 }}>
                Connect your checking account to automatically track your balance
                and spending. Coming in a future update.
              </div>
              <span className="badge badge--amber">🔗 Coming soon</span>
            </div>
          </div>
        </SettingsSection>

        {/* Parent connection */}
        <SettingsSection title="Parent connection">
          {family?.parentId ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 20 }}>🔗</span>
              <div>
                <div style={{ fontWeight: 600 }}>Connected</div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-3)', marginTop: 2 }}>
                  Your parent can see your spending summary
                </div>
              </div>
              <span className="badge badge--green" style={{ marginLeft: 'auto' }}>Active</span>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <p style={{ color: 'var(--color-text-2)', fontSize: '0.9rem', marginBottom: 16 }}>
                Not connected to a parent account yet
              </p>
              <button className="btn btn--secondary btn--full"
                onClick={() => navigate('/invite/pending')}>
                Connect with parent
              </button>
            </div>
          )}
        </SettingsSection>

        {/* Help & Support */}
        <SettingsSection title="Help & support">
          <a
            href="https://campuswallet.app/help"
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'flex', alignItems: 'center', gap: 12,
              padding: '6px 0', textDecoration: 'none' }}>
            <span style={{ fontSize: 18 }}>❓</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)' }}>Help Center</div>
              <div style={{ fontWeight: 600, marginTop: 2, fontSize: '0.9375rem',
                color: 'var(--color-primary)' }}>campuswallet.app/help</div>
            </div>
            <span style={{ color: 'var(--color-text-3)', fontSize: '0.875rem' }}>→</span>
          </a>
        </SettingsSection>

        {/* Sign out */}
        <button
          className="btn btn--ghost btn--full"
          style={{ color: 'var(--color-error)', marginTop: 8 }}
          onClick={handleSignOut}
        >
          Sign out
        </button>

        <p style={{ textAlign: 'center', fontSize: '0.75rem', color: 'var(--color-text-3)' }}>
          Campus Wallet · Beta · v0.1
        </p>
      </div>
    </div>
  );
};

const SettingsSection = ({ title, children }) => (
  <div>
    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--color-text-3)',
      letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>
      {title}
    </div>
    <div className="card">{children}</div>
  </div>
);

const SettingsRow = ({ icon, label, value }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12,
    padding: '6px 0', borderBottom: '1px solid var(--color-border)' }}
    className="settings-row">
    <span style={{ fontSize: 18 }}>{icon}</span>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)' }}>{label}</div>
      <div style={{ fontWeight: 600, marginTop: 2, fontSize: '0.9375rem' }}>{value}</div>
    </div>
  </div>
);

export default StudentSettings;
